import DividerWithText from "../../../../components/DividerWithText";
import {
    Box,
    MenuItem,
    InputAdornment
} from "@mui/material";
import { CustomCheckbox } from "../../../../components/CustomCheckbox";
import DivContainerBody from "../../../../components/DivContainerBody";
import React from "react";
import { CustomTextField } from "../../../../components/CustomTextField";
import { CustomSelectAndChildren } from "../../../../components/CustomSelectAndChildren";
import {DEVICE_MODEL_NAME, disableReadAfter} from "../Model/Model";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTitle: Full Stack Developer
 *
 * Device authentication section.
 * Options are enabled or disabled based on the device type.
 *
 * - disableAuthCommon: Device types for which common authentication options (QR, Bluetooth, smartCard, and PIN) are disabled.
 * - disableGroup1: Device types for which facial recognition is disabled.
 * - disableGroup2: Device types for which fingerprint reader is disabled.
 * - disableAuthSelect: Device types for which the authentication select is disabled.
 *
 * The "readAfter" field validates both empty fields and helper text based on the device type.
 *
 * @param {object} props - Component properties.
 * @param {object} props.data - Object containing device data, including deviceType.
 * @param {function} props.handleChange - Function to handle input changes.
 * @param {function} props.t - Function for text translation.
 * @param {boolean} props.emptyFields - Flag to indicate if empty fields should be highlighted.
 * @param {array} props.deviceTypeAuthentications - Array of available authentication types.
 * @param {boolean} props.formSubmitted - Flag to indicate if the form has been submitted.
 * @returns {JSX.Element} Rendered component.
 */
const SectionCreateDeviceAuthentication = ({data, handleChange, t, emptyFields, deviceTypeAuthentications,}) => {
    // Device types for which common authentication options (QR, Bluetooth, smartCard, and PIN) are disabled.
    const disableAuthCommon = [
        DEVICE_MODEL_NAME.TELPO_TPS_980,
        DEVICE_MODEL_NAME.TELPO_K10,
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR,
        DEVICE_MODEL_NAME.ARATEK_BD8500
    ];
    const disableQrCode = [
        DEVICE_MODEL_NAME.TELPO_TPS_980,
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR
    ];
    // Device types for which facial recognition is disabled.
    const disableGroup1 = [
        DEVICE_MODEL_NAME.MANTRA_MFSTAB_II,
        DEVICE_MODEL_NAME.PDA_CARIBE_PL50L,
        DEVICE_MODEL_NAME.TELPO_TPS_980,
        DEVICE_MODEL_NAME.TELPO_TPS_450,
        DEVICE_MODEL_NAME.TELPO_K10,
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR,
        DEVICE_MODEL_NAME.ARATEK_BM5510
    ];
    // Device types for which the fingerprint reader is disabled.
    const disableGroup2 = [
        DEVICE_MODEL_NAME.TELPO_TPS_980,
        DEVICE_MODEL_NAME.TELPO_K10,
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_F10,
        DEVICE_MODEL_NAME.TELPO_F6_T,
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.CAMERA_LPR,  
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.ARATEK_BD8500];
    // For the authentication select, add device type 6 to the common disable list.
    const disableAuthSelect = [
        DEVICE_MODEL_NAME.TELPO_TPS_980,
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_K10,
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR,
        DEVICE_MODEL_NAME.ARATEK_BD8500];

    const disablePin = [
        DEVICE_MODEL_NAME.ARATEK_BD8500];

    const validateReadAfter = !disableReadAfter.includes(data.deviceType);


    return (
        <>
            <DivContainerBody>
                <div className={"row"}>
                    <div className={"col-12"}>
                        <DividerWithText title={t("authentication")} spacing={"mb-4"} />
                    </div>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_facial_recognition"
                            checked={data.facialRecognition}
                            value={data.facialRecognition}
                            onChange={(e) => handleChange(e)}
                            labelId="lbl_cud_facialRecognition"
                            label="facial_recognition"
                            trans="facial_recognition"
                            name="facialRecognition"
                            disabled={disableGroup1.includes(data.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_fingerprint"
                            checked={data.fingerprint}
                            value={data.fingerprint}
                            onChange={(e) => handleChange(e)}
                            labelId="lbl_cud_fingerprint"
                            label="fingerprint_reader"
                            trans="fingerprint_reader"
                            name="fingerprint"
                            disabled={disableGroup2.includes(data.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_qr"
                            checked={data.qr}
                            value={data.qr}
                            onChange={(e) => handleChange(e)}
                            labelId="lbl_cud_qr"
                            label="qr_code_reader"
                            trans="qr_code_reader"
                            name="qr"
                            disabled={disableQrCode.includes(data.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_bluetooth"
                            checked={data.bluetooth}
                            value={data.bluetooth}
                            onChange={(e) => handleChange(e)}
                            labelId="lbl_cud_bluetooth"
                            label="bluetooth_reader"
                            trans="bluetooth_reader"
                            name="bluetooth"
                            disabled={true}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_smart_card"
                            checked={data.smartCard}
                            value={data.smartCard}
                            onChange={(e) => handleChange(e)}
                            labelId="lbl_cud_smartCard"
                            label="rfid_reader"
                            trans="rfid_reader"
                            name="smartCard"
                            disabled={disableAuthCommon.includes(data.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_pin"
                            checked={data.pin}
                            value={data.pin}
                            onChange={(e) => handleChange(e)}
                            labelId="lbl_cud_pin"
                            label="pin"
                            trans="pin"
                            name="pin"
                            disabled={!disablePin.includes(data.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3">
                        <CustomSelectAndChildren
                            id="txt_cud_authentication"
                            name="authentication"
                            emptyFields={emptyFields && !disableAuthSelect.includes(data.deviceType)}
                            value={data?.authentication}
                            trans="authentication"
                            disabled={disableAuthSelect.includes(data.deviceType) || !data?.deviceType}
                            required={true}
                            onChange={(evt) => { handleChange(evt, "authentication") }}
                            showNoSelectionOption={false}
                        >
                            {deviceTypeAuthentications &&
                            deviceTypeAuthentications.map((item, index) => (
                                <MenuItem key={index} value={item}>
                                    {(item === 1 && t("none").toUpperCase()) ||
                                    (item === 3 && t("qr_+_face").toUpperCase()) ||
                                    (item === 2 && t("qr_+_fingerprint").toUpperCase())}
                                </MenuItem>
                            ))}
                        </CustomSelectAndChildren>
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3">
                        <CustomTextField
                            id="txt_cud_read_again_after"
                            name="readAfter"
                            emptyFields={emptyFields && validateReadAfter}
                            value={data?.readAfter}
                            trans="read_again_after"
                            required={true}
                            onChange={(e) => {
                                const value = e.target.value;
                                const regex = /^[0-9]*$/;
                                if (regex.test(value) && (value === "" || parseInt(value, 10) >= 1)) {
                                    handleChange(e);
                                }
                            }}
                            disabled={disableReadAfter.includes(data.deviceType)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment className="endAdornmentStyle" position="end">
                                        {t("sec")}
                                    </InputAdornment>
                                ),
                            }}
                            length={4}
                            helperText={t("this_value_must_be_greater_than_0")}
                        />
                    </Box>
                </div>
            </DivContainerBody>
        </>
    );
};

export default SectionCreateDeviceAuthentication;
